import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Twitter,
  Facebook,
  Instagram,
  MessageCircle,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  Flame,
  Droplets,
  Mountain,
  Wind,
  Filter,
  RefreshCw,
  Heart,
  Repeat,
  MessageSquare
} from 'lucide-react';
import SocialMediaService from '../services/socialMediaService';
import type { SocialMediaPost } from '../services/socialMediaService';

const SocialMediaFeed: React.FC = () => {
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const data = await SocialMediaService.getDisasterPosts();
      setPosts(data);
      setLoading(false);
    };

    fetchPosts();
    const interval = setInterval(fetchPosts, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getPlatformIcon = (platform: string) => {
    switch(platform) {
      case 'twitter': return <Twitter size={16} className="text-cyan-400" />;
      case 'facebook': return <Facebook size={16} className="text-blue-400" />;
      case 'instagram': return <Instagram size={16} className="text-pink-400" />;
      case 'whatsapp': return <MessageCircle size={16} className="text-green-400" />;
      default: return <MessageCircle size={16} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch(severity) {
      case 'critical': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-green-500 border-green-500 bg-green-500/10';
      default: return 'text-gray-500';
    }
  };

  const getDisasterIcon = (type?: string) => {
    switch(type) {
      case 'flood': return <Droplets size={14} className="text-blue-500" />;
      case 'fire': return <Flame size={14} className="text-orange-500" />;
      case 'earthquake': return <Mountain size={14} className="text-brown-500" />;
      case 'cyclone': return <Wind size={14} className="text-purple-500" />;
      case 'collapse': return <AlertTriangle size={14} className="text-red-500" />;
      default: return null;
    }
  };

  const filteredPosts = posts.filter(post => {
    if (filter !== 'all' && post.disasterType !== filter) return false;
    if (severityFilter !== 'all' && post.severity !== severityFilter) return false;
    return true;
  });

  if (loading && posts.length === 0) {
    return (
      <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="animate-spin text-cyan-500 mr-3" />
          <span className="text-gray-400">Loading social media feeds...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <MessageCircle className="text-blue-400" />
            SOCIAL MEDIA MONITORING
          </h2>
          <p className="text-xs text-gray-400 mt-1">Real-time disaster mentions from social platforms</p>
        </div>
        
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-black/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Disasters</option>
            <option value="flood">Floods</option>
            <option value="fire">Fires</option>
            <option value="earthquake">Earthquakes</option>
            <option value="cyclone">Cyclones</option>
            <option value="collapse">Collapses</option>
          </select>
          
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="bg-black/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
        <AnimatePresence>
          {filteredPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.05 }}
              className="bg-black/30 rounded-lg p-4 border border-gray-700 hover:border-cyan-500/50 transition-all"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  {getPlatformIcon(post.platform)}
                  <span className="font-bold text-sm">{post.author}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(post.severity)}`}>
                  {post.severity.toUpperCase()}
                </span>
              </div>

              <p className="text-sm mb-3">{post.content}</p>

              <div className="flex items-center gap-4 mb-3 text-xs text-gray-400">
                {post.location && (
                  <div className="flex items-center gap-1">
                    <MapPin size={12} />
                    {post.location}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Clock size={12} />
                  {new Date(post.timestamp).toLocaleTimeString()}
                </div>
                {post.disasterType && (
                  <div className="flex items-center gap-1">
                    {getDisasterIcon(post.disasterType)}
                    <span className="capitalize">{post.disasterType}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-400 border-t border-gray-700 pt-2">
                <div className="flex items-center gap-1">
                  <Heart size={12} className="text-red-400" />
                  {post.likes}
                </div>
                <div className="flex items-center gap-1">
                  <Repeat size={12} className="text-green-400" />
                  {post.shares}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SocialMediaFeed;